/**
 * The credential fields' rules, shared by the two screens that ask for them.
 *
 * Its own module because there are now two: sign-in and the reset request ask
 * for the same account alias and the same address, validate them the same way,
 * and read the same remembered alias. Two copies of a validation rule is two
 * rules by the third change — and the failure mode is quiet, because a screen
 * that is slightly more permissive than the other just sends a request the API
 * refuses for a reason neither screen explains.
 */

export const ACCOUNT_ALIAS_PATTERN = /^[A-Za-z0-9-]{3,50}$/

/**
 * Deliberately permissive. The API is the authority on whether an address is
 * deliverable; this only catches the typo of omitting the @ entirely, so nobody
 * is refused by the client over an address their organisation actually uses.
 */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Where the last-used account alias is kept.
 *
 * An organisation's short code is not a secret, and retyping it at every shift
 * change on a shared terminal is friction with no security value. The email and
 * password are never remembered.
 */
export const ACCOUNT_ALIAS_STORAGE = 'forge.accountAlias'

/**
 * The remembered alias, or an empty string.
 *
 * Wrapped because `localStorage` THROWS rather than returning null in a private
 * window and under a blocked-site-data policy — so an unguarded read here would
 * take the sign-in screen down entirely for the people least able to work around
 * it.
 */
export function readStoredAlias() {
  try {
    return window.localStorage.getItem(ACCOUNT_ALIAS_STORAGE) ?? ''
  } catch {
    return ''
  }
}

/** Remembers it, or quietly does not. Signing in still works either way. */
export function rememberAlias(alias) {
  try {
    // Stored exactly as typed. The API matches the alias case-sensitively, so
    // normalising the case here would break every subsequent sign-in.
    window.localStorage.setItem(ACCOUNT_ALIAS_STORAGE, alias.trim())
  } catch {
    /* Private browsing. */
  }
}
